import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast, Toaster } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, UserCog, Trash2, Pencil, KeyRound, Copy } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "coordinator" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", role: "coordinator", active: true });
  const [resetting, setResetting] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const doReset = async (u) => {
    try {
      const r = await api.post(`/users/${u.id}/reset-password`);
      setResetResult(r.data); setResetting(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal reset password"); }
  };

  const openEdit = (u) => { setEditForm({ name: u.name, role: u.role, active: u.active }); setEditUser(u); };
  const saveEdit = async () => {
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      toast.success("Akun diperbarui"); setEditUser(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const activeBMs = users.filter((u) => u.role === "branch_manager" && u.active);
  const isLastBM = (u) => u && u.role === "branch_manager" && u.active && activeBMs.length <= 1;

  const save = async () => {
    try {
      await api.post("/users", form);
      toast.success("Pengguna dibuat"); setDialog(false);
      setForm({ name: "", email: "", password: "", role: "coordinator" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const deactivate = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Dinonaktifkan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <Toaster position="top-center" richColors />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-nunito font-extrabold text-2xl text-gtsnavy">Manajemen Pengguna</h2>
          <p className="text-sm text-muted-foreground">Kelola akun & hak akses</p>
        </div>
        <Button onClick={() => setDialog(true)} className="rounded-full bg-gradient-to-r from-gtsorange to-gtsorange-light text-white gap-1" data-testid="add-user-btn">
          <Plus className="w-4 h-4" /> Tambah
        </Button>
      </div>

      <Card className="rounded-2xl border-none shadow-[0_8px_30px_rgb(16,42,86,0.06)] bg-white overflow-hidden">
        <div className="overflow-x-auto gts-scroll">
          <table className="w-full text-sm">
            <thead><tr className="bg-gtsnavy text-white">
              <th className="text-left px-4 py-3">Nama</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={u.id} className={i % 2 ? "bg-orange-50/30" : ""} data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-gtsnavy">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-full ${u.role === "branch_manager" ? "bg-gtsnavy text-white" : u.role === "teacher" ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-gtsorange"}`}>
                      {u.role === "branch_manager" ? "Branch Manager" : u.role === "teacher" ? "Guru / Teacher" : "Coordinator"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={`rounded-full ${u.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                      {u.active ? "Aktif" : "Nonaktif"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="rounded-full mr-2" data-testid={`edit-user-${u.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {u.id !== user.id && u.active && (
                      <Button size="sm" variant="outline" onClick={() => setResetting(u)} className="rounded-full mr-2 text-gtsnavy border-gtsnavy/30" data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {u.id !== user.id && u.active && !isLastBM(u) && (
                      <Button size="sm" variant="outline" onClick={() => deactivate(u)} className="rounded-full text-rose-600 border-rose-200" data-testid={`deactivate-user-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Tambah Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" data-testid="user-name-input" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" data-testid="user-email-input" /></div>
            <div><Label>Password Awal</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1" data-testid="user-password-input" /></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coordinator">Coordinator GTS</SelectItem>
                  <SelectItem value="teacher">Guru / Teacher</SelectItem>
                  <SelectItem value="branch_manager">Branch Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full bg-gtsnavy text-white" data-testid="save-user-btn">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Akun · {editUser?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-xl mt-1" data-testid="edit-user-name" /></div>
            <div>
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })} disabled={isLastBM(editUser)}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="coordinator">Coordinator GTS</SelectItem>
                  <SelectItem value="teacher">Guru / Teacher</SelectItem>
                  <SelectItem value="branch_manager">Branch Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-xl border p-3">
              <div><p className="text-sm font-medium">Akun Aktif</p><p className="text-xs text-muted-foreground">Nonaktifkan untuk memblokir login</p></div>
              <Switch checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} disabled={isLastBM(editUser)} data-testid="edit-user-active" />
            </div>
            {isLastBM(editUser) && (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2" data-testid="last-bm-note">
                Ini Branch Manager terakhir yang aktif. Role & status tidak dapat diubah agar sistem tidak terkunci.
              </p>
            )}
          </div>
          <DialogFooter><Button onClick={saveEdit} className="rounded-full bg-gtsnavy text-white" data-testid="save-edit-user-btn">Simpan Perubahan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(o) => !o && setResetting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Buat password baru untuk <b className="text-gtsnavy">{resetting?.name}</b> ({resetting?.email})? Password lama akan langsung tidak berlaku, dan user diminta ganti password saat login berikutnya.
          </p>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setResetting(null)} data-testid="cancel-reset-btn">Batal</Button>
            <Button className="rounded-full bg-gtsnavy text-white" onClick={() => doReset(resetting)} data-testid="confirm-reset-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetResult} onOpenChange={(o) => !o && setResetResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Password Baru Dibuat</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Bagikan password ini ke <b className="text-gtsnavy">{resetResult?.name}</b>. Password hanya ditampilkan sekali di sini.</p>
            <div className="rounded-xl border p-3 bg-slate-50 space-y-2">
              <div>
                <p className="text-xs text-muted-foreground">Email</p>
                <p className="font-medium text-gtsnavy" data-testid="reset-result-email">{resetResult?.email}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Password Baru</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="font-bold text-gtsnavy text-lg tracking-wide" data-testid="new-password-value">{resetResult?.password}</code>
                  <Button size="sm" variant="outline" className="rounded-full gap-1" onClick={() => { navigator.clipboard?.writeText(resetResult.password); toast.success("Password disalin"); }} data-testid="copy-password-btn"><Copy className="w-3.5 h-3.5" /> Salin</Button>
                </div>
              </div>
            </div>
            <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">User akan diminta mengganti password saat login berikutnya.</p>
          </div>
          <DialogFooter><Button className="rounded-full bg-gtsnavy text-white" onClick={() => setResetResult(null)} data-testid="close-reset-result-btn">Selesai</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
